import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import CheckCircleIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorIcon from '@mui/icons-material/ErrorRounded';
import WarningIcon from '@mui/icons-material/WarningRounded';
import InfoIcon from '@mui/icons-material/InfoRounded';
import CloseIcon from '@mui/icons-material/Close';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { notificationsApi } from '@/api/notifications';
import { useBusinessScope } from '@/hooks/useBusinessScope';
import { PALETTE } from '@/theme/tokens';

dayjs.extend(relativeTime);

/**
 * The notification bell.
 *
 * Replaces a hardcoded `badgeContent={3}` and two invented rows that had been
 * sitting in the top bar telling everyone the same two things forever.
 *
 * Two queries rather than one: the badge polls a cheap indexed COUNT every 60
 * seconds, and the list is only fetched when the panel is actually open. A
 * closed bell costs one small integer a minute.
 */

const LEVEL = {
  success: { color: '#16A34A', tint: '#DCFCE7', Icon: CheckCircleIcon },
  error: { color: '#DC2626', tint: '#FEE2E2', Icon: ErrorIcon },
  warning: { color: '#D97706', tint: '#FEF3C7', Icon: WarningIcon },
  info: { color: '#4F46E5', tint: '#E0E7FF', Icon: InfoIcon },
};

export default function NotificationsBell() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { businessId } = useBusinessScope();
  const [anchor, setAnchor] = useState(null);

  const open = !!anchor;
  const scope = businessId ? { business_id: businessId } : {};

  const { data: countData } = useQuery({
    queryKey: ['notifications', 'count'],
    queryFn: () => notificationsApi.unreadCount(),
    refetchInterval: 60_000,
    // Nobody needs a badge kept warm on a tab they aren't looking at.
    refetchIntervalInBackground: false,
    staleTime: 30_000,
  });

  const { data: listData, isLoading } = useQuery({
    queryKey: ['notifications', 'list', businessId || 'all'],
    queryFn: () => notificationsApi.list({ ...scope, per_page: 20 }),
    enabled: open,
  });

  const unread = countData?.unread_count ?? 0;
  const items = listData?.data ?? [];

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['notifications'] });

  const markRead = useMutation({
    mutationFn: (id) => notificationsApi.markRead(id),
    onSuccess: refresh,
  });
  const markAll = useMutation({
    mutationFn: () => notificationsApi.markAllRead(scope),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (id) => notificationsApi.remove(id),
    onSuccess: refresh,
  });

  const openItem = (n) => {
    if (!n.read_at) markRead.mutate(n.id);
    setAnchor(null);
    // action_url is always a stored SPA path, never an absolute URL — see the
    // migration for why that distinction is deliberate.
    if (n.action_url) navigate(n.action_url);
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton onClick={(e) => setAnchor(e.currentTarget)}>
          <Badge badgeContent={unread} color="error" max={99}>
            <NotificationsNoneIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              width: 396, maxHeight: 520, borderRadius: 3, mt: 1,
              boxShadow: '0 20px 48px -12px rgba(20,25,60,.28)',
              border: `1px solid ${PALETTE.cardBorder}`,
            },
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 1.5 }}
        >
          <Typography sx={{ fontWeight: 800, fontSize: 15 }}>
            Notifications
            {unread > 0 && (
              <Typography component="span" sx={{ ml: 0.75, fontSize: 13, color: 'text.secondary', fontWeight: 600 }}>
                {unread} new
              </Typography>
            )}
          </Typography>
          {unread > 0 && (
            <Button
              size="small"
              startIcon={<DoneAllIcon sx={{ fontSize: 16 }} />}
              onClick={() => markAll.mutate()}
              disabled={markAll.isPending}
              sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12.5 }}
            >
              Mark all read
            </Button>
          )}
        </Stack>

        <Divider />

        {isLoading && (
          <Box sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={22} />
          </Box>
        )}

        {!isLoading && items.length === 0 && (
          <Box sx={{ py: 5, px: 3, textAlign: 'center' }}>
            <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 0.5 }}>You&apos;re all caught up</Typography>
            <Typography variant="caption" color="text.secondary">
              Published posts, failures and anything needing your attention will show up here.
            </Typography>
          </Box>
        )}

        {items.map((n) => {
          const { color, tint, Icon } = LEVEL[n.level] || LEVEL.info;
          return (
            <Stack
              key={n.id}
              direction="row"
              spacing={1.5}
              onClick={() => openItem(n)}
              sx={{
                px: 2, py: 1.5,
                cursor: n.action_url ? 'pointer' : 'default',
                position: 'relative',
                borderBottom: `1px solid ${PALETTE.cardBorder}`,
                // Unread carries a tint AND a dot, so it survives being
                // printed, screenshotted, or read by someone colourblind.
                bgcolor: n.read_at ? 'transparent' : 'rgba(61,90,254,.045)',
                transition: 'background .15s',
                '&:hover': { bgcolor: PALETTE.workspace },
                '&:hover .notifDismiss': { opacity: 1 },
              }}
            >
              <Box
                sx={{
                  width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: tint, color,
                }}
              >
                <Icon sx={{ fontSize: 18 }} />
              </Box>

              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontWeight: n.read_at ? 600 : 800, fontSize: 13.5, lineHeight: 1.35 }}>
                  {n.title}
                </Typography>
                {n.body && (
                  <Typography
                    sx={{
                      fontSize: 12.5, color: 'text.secondary', mt: 0.25, lineHeight: 1.45,
                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {n.body}
                  </Typography>
                )}
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                  <Typography variant="caption" color="text.disabled">
                    {dayjs(n.created_at).fromNow()}
                  </Typography>
                  {n.action_label && (
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'primary.main' }}>
                      {n.action_label}
                    </Typography>
                  )}
                </Stack>
              </Box>

              {!n.read_at && (
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'primary.main', mt: 1, flexShrink: 0 }} />
              )}

              <IconButton
                className="notifDismiss"
                size="small"
                aria-label="Dismiss notification"
                onClick={(e) => { e.stopPropagation(); remove.mutate(n.id); }}
                sx={{
                  position: 'absolute', top: 6, right: 6,
                  opacity: 0, transition: 'opacity .15s',
                  color: 'text.disabled',
                }}
              >
                <CloseIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Stack>
          );
        })}
      </Popover>
    </>
  );
}
